import React from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useSubscription} from '@/shared/utils'
import {useLayoutPanels} from '@/shared/context/ChatContext'
import {HeaderLogo} from './ThemeAwareLogo'
import {LayoutPanelToggles} from './LayoutPanelToggles'
import {ProfilePhoto} from './header/ProfilePhoto'

/**
 * Every entry here navigates. The lateral panels (Files, AI Chat) are a layout state rather than a
 * destination, so they live in LayoutPanelToggles instead — an active underline in this bar always
 * means "this is the page you are on".
 */
type MenuItem = {
  id: string
  label: string
  iconClass: string
  path: string
  requiresSubscription: boolean
}

export const HorizontalMenuBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {hasSubscription} = useSubscription()
  const {expandCenter} = useLayoutPanels()

  const allMenuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      iconClass: 'fas fa-chart-pie',
      path: '/dashboard',
      requiresSubscription: false,
    },
    {
      id: 'tools',
      label: 'Tools',
      iconClass: 'fas fa-toolbox',
      path: '/tools',
      requiresSubscription: true,
    },
    {
      id: 'jobs',
      label: 'Jobs',
      iconClass: 'fas fa-flask',
      path: '/jobs',
      requiresSubscription: true,
    },
    {
      id: 'results',
      label: 'Results',
      iconClass: 'fas fa-chart-line',
      path: '/results',
      requiresSubscription: true,
    },
    {
      id: 'workflows',
      label: 'Workflows',
      iconClass: 'fas fa-diagram-project',
      path: '/workflows',
      requiresSubscription: true,
    },
    {
      id: 'admin',
      label: 'Settings',
      iconClass: 'fas fa-gear',
      path: '/admin',
      requiresSubscription: false,
    },
  ]

  // PRODUCTION: Feature flags to hide menu items temporarily
  const SHOW_TOOLS_MENU = false
  const SHOW_WORKFLOWS_MENU = false
  const SHOW_SETTINGS_MENU = false

  // Filter menu items based on subscription availability and feature flags
  const menuItems = allMenuItems.filter(item => {
    const hasAccess = !item.requiresSubscription || hasSubscription
    const isToolsHidden = item.id === 'tools' && !SHOW_TOOLS_MENU
    const isWorkflowsHidden = item.id === 'workflows' && !SHOW_WORKFLOWS_MENU
    const isSettingsHidden = item.id === 'admin' && !SHOW_SETTINGS_MENU
    return hasAccess && !isToolsHidden && !isWorkflowsHidden && !isSettingsHidden
  })

  const isActive = (item: MenuItem) => location.pathname === item.path

  const handleMenuNavigate = (path: string) => {
    // Same-route clicks do not change location.pathname, so expand here — not only in a
    // pathname effect — otherwise Results→collapse→Results leaves the centre hidden.
    expandCenter()
    navigate(path)
  }

  return (
    <div className='ob-menu-bar'>
      <div className='ob-menu-bar__container'>
        <div className='ob-menu-bar__logo'>
          <HeaderLogo to='/dashboard' />
        </div>
        <div className='ob-menu-bar__nav'>
          {menuItems.map((item) => (
            <div
              key={item.id}
              data-testid={`menu-item-${item.id}`}
              onClick={() => handleMenuNavigate(item.path)}
              className={`ob-menu-bar__menu-item ${
                isActive(item) ? 'ob-menu-bar__menu-item--active' : ''
              }`}
            >
              <span className='ob-menu-bar__menu-icon'><i className={item.iconClass} /></span>
              <span className='ob-menu-bar__menu-text'>{item.label}</span>
            </div>
          ))}
        </div>
        <LayoutPanelToggles />
        <div className='ob-menu-bar__profile'>
          <ProfilePhoto />
        </div>
      </div>
    </div>
  )
}
